export interface HoverInspectorHandlers {
  onSelectWidget: (widgetId: string) => void;
  onMoveUp: (widgetId: string) => void;
  onMoveDown: (widgetId: string) => void;
  onDelete: (widgetId: string, widgetName?: string) => void;
  onInsertBetween?: (
    targetWidgetId: string,
    position: 'before' | 'after',
    isSubItem: boolean
  ) => void;
}

export class HoverInspector {
  private box: HTMLElement | null = null;
  private badge: HTMLElement | null = null;
  private btnInsertTop: HTMLButtonElement | null = null;
  private btnInsertBottom: HTMLButtonElement | null = null;
  private lineTop: HTMLElement | null = null;
  private lineBottom: HTMLElement | null = null;
  private isEnabled: boolean = false;
  private currentTarget: HTMLElement | null = null;
  private handlers: HoverInspectorHandlers | null = null;
  private pageId: string | null = null;

  public init(handlers: HoverInspectorHandlers): void {
    this.handlers = handlers;

    this.box = document.createElement('div');
    this.box.className = 've-hover-box loumars-visual-inspector-overlay';
    this.box.id = 've-hover-box';
    this.box.setAttribute('data-id', 'loumars-visual-inspector-overlay');

    this.lineTop = document.createElement('div');
    this.lineTop.className = 've-insert-line ve-insert-line-top';
    this.box.appendChild(this.lineTop);

    this.lineBottom = document.createElement('div');
    this.lineBottom.className = 've-insert-line ve-insert-line-bottom';
    this.box.appendChild(this.lineBottom);

    this.btnInsertTop = document.createElement('button');
    this.btnInsertTop.type = 'button';
    this.btnInsertTop.className = 've-insert-btn ve-insert-top loumars-insert-top-btn';
    this.btnInsertTop.id = 've-insert-top';
    this.btnInsertTop.setAttribute('aria-label', 'Add block above');
    this.btnInsertTop.setAttribute('title', 'Add block above');
    this.btnInsertTop.innerHTML = '+';
    this.box.appendChild(this.btnInsertTop);

    this.btnInsertBottom = document.createElement('button');
    this.btnInsertBottom.type = 'button';
    this.btnInsertBottom.className = 've-insert-btn ve-insert-bottom loumars-insert-bottom-btn';
    this.btnInsertBottom.id = 've-insert-bottom';
    this.btnInsertBottom.setAttribute('aria-label', 'Add block below');
    this.btnInsertBottom.setAttribute('title', 'Add block below');
    this.btnInsertBottom.innerHTML = '+';
    this.box.appendChild(this.btnInsertBottom);

    this.badge = document.createElement('div');
    this.badge.className = 've-hover-badge loumars-visual-inspector-badge';
    this.badge.id = 've-hover-badge';
    this.badge.setAttribute('data-id', 'loumars-visual-inspector-badge');
    this.box.appendChild(this.badge);

    document.body.appendChild(this.box);

    document.addEventListener('mouseover', this.handleMouseOver.bind(this));
    window.addEventListener('scroll', this.updatePosition.bind(this), { passive: true });
    window.addEventListener('resize', this.updatePosition.bind(this), { passive: true });

    this.btnInsertTop.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (!this.currentTarget) return;
      const id = this.resolveBlockId(this.currentTarget);
      const isSub = this.isSubItemElement(this.currentTarget);
      if (id) {
        this.handlers?.onInsertBetween?.(id, 'before', isSub);
      }
    });

    this.btnInsertBottom.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (!this.currentTarget) return;
      const id = this.resolveBlockId(this.currentTarget);
      const isSub = this.isSubItemElement(this.currentTarget);
      if (id) {
        this.handlers?.onInsertBetween?.(id, 'after', isSub);
      }
    });

    document.addEventListener(
      'click',
      (e) => {
        if (!this.isEnabled) return;
        const target = e.target as Element | null;
        if (!target) return;

        if (
          target.closest('#loumars-visual-editor-root') ||
          target.closest('#ve-dock') ||
          target.closest('.ve-drawer') ||
          target.closest('.ve-hover-badge') ||
          target.closest('.ve-hover-box') ||
          target.closest('.ve-status-toast') ||
          target.closest('.ve-drawer-backdrop') ||
          target.closest('.ve-modal-backdrop') ||
          target.closest('#ve-confirm-modal')
        ) {
          return;
        }

        const block = this.findBlock(target);
        if (block) {
          const id = this.resolveBlockId(block);
          if (id) {
            e.preventDefault();
            e.stopPropagation();
            this.handlers?.onSelectWidget(id);
          }
        }
      },
      true
    );
  }

  public setPageId(pageId?: string | null): void {
    this.pageId = pageId || null;
  }

  private isPageId(id?: string | null): boolean {
    if (!id || !this.pageId) return false;
    return id.trim().toLowerCase() === this.pageId.trim().toLowerCase();
  }

  public isSubItemElement(el: HTMLElement): boolean {
    const type = el.getAttribute('data-widget-type');
    if (
      type &&
      /^(FeatureItem|AccordionItem|CarouselSlide|LinkMenuItem|ContentMenuItem|HtmlMenuItem|TeamMember|PricingPlan|GalleryItem|TestimonialItem|LogoItem|TabItem|CardItem|CounterItem|JobPostingItem)$/i.test(
        type
      )
    ) {
      return true;
    }
    const directId = el.getAttribute('data-content-item-id');
    if (directId) {
      const parentContainer = el.parentElement?.closest<HTMLElement>(
        '[data-content-item-id], [data-widget-type], section, .loumars-flow-widget-wrapper'
      );
      if (parentContainer) {
        const parentId = parentContainer.getAttribute('data-content-item-id');
        if (parentId && parentId !== directId && !this.isPageId(parentId)) {
          return true;
        }
      }
    }
    return false;
  }

  public resolveBlockId(block: HTMLElement): string {
    // 1. Direct data-content-item-id on block
    const directId = block.getAttribute('data-content-item-id');
    if (directId && !this.isPageId(directId)) {
      return directId;
    }

    // 2. Nearest parent wrapper (e.g. .loumars-flow-widget-wrapper) that has data-content-item-id
    const wrapper = block.closest<HTMLElement>(
      '.loumars-flow-widget-wrapper[data-content-item-id], [data-widget-id]'
    );
    if (wrapper) {
      const wrapperId =
        wrapper.getAttribute('data-content-item-id') || wrapper.getAttribute('data-widget-id');
      if (wrapperId && !this.isPageId(wrapperId)) {
        return wrapperId;
      }
    }

    // 3. Fallback to widget type or id (never query children)
    return block.getAttribute('data-widget-type') || block.id || '';
  }

  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (!enabled) {
      this.hide();
    }
  }

  private handleMouseOver(e: MouseEvent): void {
    if (!this.isEnabled) return;
    const target = e.target as Element | null;
    if (!target) return;

    if (
      target.closest('#loumars-visual-editor-root') ||
      target.closest('#ve-dock') ||
      target.closest('.ve-drawer') ||
      target.closest('.ve-hover-badge') ||
      target.closest('.ve-hover-box') ||
      target.closest('.ve-status-toast') ||
      target.closest('.ve-drawer-backdrop') ||
      target.closest('.ve-modal-backdrop') ||
      target.closest('#ve-confirm-modal')
    ) {
      return;
    }

    const block = this.findBlock(target);
    if (block && block !== this.currentTarget) {
      this.currentTarget = block;
      this.show(block);
    } else if (!block && this.currentTarget) {
      this.hide();
    }
  }

  public findBlock(element: Element): HTMLElement | null {
    if (!element || element === document.body || element === document.documentElement) return null;
    const candidate = element.closest<HTMLElement>(
      '[data-content-item-id], [data-widget-type], section, [class*="widget-"], .loumars-flow-widget-wrapper'
    );
    if (!candidate || candidate === document.body || candidate === document.documentElement) {
      return null;
    }
    if (candidate.tagName === 'MAIN' || candidate.tagName === 'ARTICLE') {
      const id = candidate.getAttribute('data-content-item-id');
      if (this.isPageId(id)) return null;
    }
    return candidate;
  }

  private show(block: HTMLElement): void {
    if (!this.box || !this.badge) return;

    const id = this.resolveBlockId(block);
    const isSub = this.isSubItemElement(block);
    const type =
      block.getAttribute('data-widget-type') ||
      block.className.match(/(widget-[a-z0-9-]+)/i)?.[1]?.replace('widget-', '') ||
      'Block';
    const cleanType = type.charAt(0).toUpperCase() + type.slice(1);

    const editLabel = isSub ? `✏️ Edit Item: ${cleanType}` : `✏️ Edit: ${cleanType}`;

    this.badge.innerHTML = `
      <span class="ve-badge-btn btn-edit loumars-visual-inspector-edit-btn" id="loumars-visual-inspector-edit-btn" title="${editLabel}">${editLabel}</span>
      <button type="button" class="ve-badge-btn btn-up" title="Move up">↑</button>
      <button type="button" class="ve-badge-btn btn-down" title="Move down">↓</button>
      <button type="button" class="ve-badge-btn btn-del" title="Delete">✕</button>
    `;

    if (this.btnInsertTop) {
      const topTitle = isSub ? 'Insert item above' : 'Add block above';
      this.btnInsertTop.setAttribute('title', topTitle);
      this.btnInsertTop.setAttribute('aria-label', topTitle);
    }

    if (this.btnInsertBottom) {
      const bottomTitle = isSub ? 'Insert item below' : 'Add block below';
      this.btnInsertBottom.setAttribute('title', bottomTitle);
      this.btnInsertBottom.setAttribute('aria-label', bottomTitle);
    }

    const editBtn = this.badge.querySelector('.btn-edit');
    const upBtn = this.badge.querySelector('.btn-up');
    const downBtn = this.badge.querySelector('.btn-down');
    const delBtn = this.badge.querySelector('.btn-del');

    editBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handlers?.onSelectWidget(id);
    });

    upBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handlers?.onMoveUp(id);
    });

    downBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handlers?.onMoveDown(id);
    });

    delBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handlers?.onDelete(id, cleanType);
    });

    this.updatePosition();
    this.box.style.display = 'block';
  }

  private updatePosition(): void {
    if (!this.currentTarget || !this.box) return;
    const rect = this.currentTarget.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;

    this.box.style.width = `${rect.width}px`;
    this.box.style.height = `${rect.height}px`;
    this.box.style.top = `${rect.top + scrollY}px`;
    this.box.style.left = `${rect.left + scrollX}px`;
  }

  public hide(): void {
    if (this.box) {
      this.box.style.display = 'none';
    }
    this.currentTarget = null;
  }

  public destroy(): void {
    this.box?.remove();
    this.box = null;
    this.badge = null;
    this.btnInsertTop = null;
    this.btnInsertBottom = null;
    this.lineTop = null;
    this.lineBottom = null;
  }
}
